import type { LngLat, WorkArea } from "./types.ts";

/**
 * Roads are extracted once and stored in lng/lat.
 *
 * OpenFreeMap Bright serves OpenMapTiles `transportation` at a native maxzoom
 * of 14. Zooming further only overzooms those same tiles, so reading roads at
 * 14 keeps the centerlines stable when the user zooms. Extraction does not
 * follow the map's current zoom, and pan / rotate never extract again.
 */
export const ROAD_EXTRACT_ZOOM = 14;

const METERS_PER_DEG_LAT = 111_320;
/** Degenerate canvases smaller than this (half-extent) are rejected. */
const MIN_HALF_M = 1;

/**
 * Below this many segments a read made under ROAD_EXTRACT_ZOOM is read again
 * once closer tiles load; low-zoom tiles drop footpaths and service roads.
 */
export const FEW_ROADS = 24;

/**
 * `settled` is the first read for a rectangle, after the camera stops and the
 * tiles for the final view load. `upgrade` reads again from closer tiles.
 */
export type ExtractReason = "area" | "settled" | "tiles" | "upgrade" | "pan" | "zoom" | "rotate";

/** Remembers which rectangle was extracted and whether its tiles have settled. */
export interface ExtractWatch {
  areaKey: string | null;
  /** True until the road sources finish loading for this rectangle. */
  awaitingTiles: boolean;
  /**
   * True from a new rectangle until the camera is idle. Tile events during a
   * fly-to would read the old view's tiles, so they wait for the settled read.
   */
  settling?: boolean;
}

/** Frame handle for move / resize / rotate in the editor. */
export type CanvasHandle =
  | "body"
  | "n"
  | "s"
  | "e"
  | "w"
  | "ne"
  | "nw"
  | "se"
  | "sw"
  | "rotate";

export function idleExtractWatch(): ExtractWatch {
  return { areaKey: null, awaitingTiles: false };
}

/** MapLibre bearing in degrees, clockwise from north. Missing or non-finite → 0. */
export function canvasBearing(area: WorkArea): number {
  const b = area.bearing;
  return typeof b === "number" && Number.isFinite(b) ? normalizeBearing(b) : 0;
}

export function canvasCenter(area: WorkArea): LngLat {
  return [(area.west + area.east) / 2, (area.south + area.north) / 2];
}

/**
 * Half-width / half-height in meters of the unrotated extents.
 * `west`/`east`/`south`/`north` always describe that north-up box; `bearing`
 * rotates it around the center.
 */
export function canvasHalfSizeM(area: WorkArea): { halfW: number; halfH: number } {
  const c = canvasCenter(area);
  const cos = Math.cos((c[1] * Math.PI) / 180);
  const halfW = ((area.east - area.west) / 2) * METERS_PER_DEG_LAT * Math.max(0.01, cos);
  const halfH = ((area.north - area.south) / 2) * METERS_PER_DEG_LAT;
  return { halfW, halfH };
}

/** Build a canvas from center, full width/height in meters, and bearing. */
export function canvasFromCenterSize(
  center: LngLat,
  widthM: number,
  heightM: number,
  bearing = 0,
): WorkArea | null {
  if (!Number.isFinite(center[0]) || !Number.isFinite(center[1])) return null;
  if (!(widthM > 0) || !(heightM > 0)) return null;
  if (widthM / 2 < MIN_HALF_M || heightM / 2 < MIN_HALF_M) return null;
  const cos = Math.cos((center[1] * Math.PI) / 180);
  const lngScale = METERS_PER_DEG_LAT * Math.max(0.01, cos);
  const halfLng = widthM / 2 / lngScale;
  const halfLat = heightM / 2 / METERS_PER_DEG_LAT;
  const br = normalizeBearing(bearing);
  const box = {
    west: center[0] - halfLng,
    south: center[1] - halfLat,
    east: center[0] + halfLng,
    north: center[1] + halfLat,
  };
  if (!(box.west < box.east) || !(box.south < box.north)) return null;
  return br === 0 ? box : { ...box, bearing: br };
}

/**
 * Axis-aligned corners of the drag become a canvas. When `bearing` is 0 this
 * matches the legacy north-up box. When the map is rotated, pass the current
 * map bearing so the canvas stays screen-aligned.
 */
export function normalizeWorkArea(a: LngLat, b: LngLat, bearing = 0): WorkArea | null {
  if (!Number.isFinite(a[0]) || !Number.isFinite(a[1])) return null;
  if (!Number.isFinite(b[0]) || !Number.isFinite(b[1])) return null;
  const br = normalizeBearing(bearing);
  if (br === 0) {
    return parseWorkArea({
      west: Math.min(a[0], b[0]),
      south: Math.min(a[1], b[1]),
      east: Math.max(a[0], b[0]),
      north: Math.max(a[1], b[1]),
    });
  }
  const center: LngLat = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  const probe: WorkArea = {
    west: center[0] - 1e-4,
    south: center[1] - 1e-4,
    east: center[0] + 1e-4,
    north: center[1] + 1e-4,
    bearing: br,
  };
  const la = toLocalM(a, probe);
  return canvasFromCenterSize(center, Math.abs(la[0]) * 2, Math.abs(la[1]) * 2, br);
}

/**
 * A finite rectangle. Legacy `{west,south,east,north}` opens as bearing 0.
 * Optional `bearing` rotates that box around its center. Bad data is ignored.
 */
export function parseWorkArea(value: unknown): WorkArea | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as {
    west?: unknown;
    south?: unknown;
    east?: unknown;
    north?: unknown;
    bearing?: unknown;
    lng?: unknown;
    lat?: unknown;
    widthM?: unknown;
    heightM?: unknown;
  };

  const widthM = finite(raw.widthM);
  const heightM = finite(raw.heightM);
  const lng = finite(raw.lng);
  const lat = finite(raw.lat);
  if (lng != null && lat != null && widthM != null && heightM != null) {
    const bearing = finite(raw.bearing) ?? 0;
    return canvasFromCenterSize([lng, lat], widthM, heightM, bearing);
  }

  const west = finite(raw.west);
  const south = finite(raw.south);
  const east = finite(raw.east);
  const north = finite(raw.north);
  if (west == null || south == null || east == null || north == null) return null;
  if (!(west < east) || !(south < north)) return null;
  const bearing = finite(raw.bearing);
  if (bearing == null || normalizeBearing(bearing) === 0) {
    return { west, south, east, north };
  }
  return { west, south, east, north, bearing: normalizeBearing(bearing) };
}

/** Drop a zero bearing so saved pieces stay compatible with older clients. */
export function serializeWorkArea(area: WorkArea): WorkArea {
  const bearing = canvasBearing(area);
  if (bearing === 0) {
    return { west: area.west, south: area.south, east: area.east, north: area.north };
  }
  return {
    west: area.west,
    south: area.south,
    east: area.east,
    north: area.north,
    bearing: Math.round(bearing * 10) / 10,
  };
}

export function pointInWorkArea(point: LngLat, area: WorkArea): boolean {
  const { halfW, halfH } = canvasHalfSizeM(area);
  if (halfW <= 0 || halfH <= 0) return false;
  if (canvasBearing(area) === 0) {
    return (
      point[0] >= area.west &&
      point[0] <= area.east &&
      point[1] >= area.south &&
      point[1] <= area.north
    );
  }
  const [x, y] = toLocalM(point, area);
  return x >= -halfW && x <= halfW && y >= -halfH && y <= halfH;
}

/** Four corners in order SW → SE → NE → NW (canvas local), closed by the caller. */
export function canvasCorners(area: WorkArea): LngLat[] {
  const { halfW, halfH } = canvasHalfSizeM(area);
  return [
    fromLocalM([-halfW, -halfH], area),
    fromLocalM([halfW, -halfH], area),
    fromLocalM([halfW, halfH], area),
    fromLocalM([-halfW, halfH], area),
  ];
}

/** Axis-aligned lng/lat bounds of the (possibly rotated) canvas. */
export function canvasBounds(area: WorkArea): WorkArea {
  const corners = canvasCorners(area);
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const [lng, lat] of corners) {
    west = Math.min(west, lng);
    south = Math.min(south, lat);
    east = Math.max(east, lng);
    north = Math.max(north, lat);
  }
  return { west, south, east, north };
}

export function areaKey(area: WorkArea): string {
  const bearing = canvasBearing(area);
  const base = [area.west, area.south, area.east, area.north].map((n) => n.toFixed(6)).join(",");
  return bearing === 0 ? base : `${base}@${bearing.toFixed(2)}`;
}

/**
 * Pan, zoom, and rotate never extract. A new rectangle does. Tile updates
 * extract only while that same rectangle is still waiting for its first load.
 */
export function shouldExtractRoads(
  watch: ExtractWatch,
  area: WorkArea | null,
  reason: ExtractReason,
): boolean {
  if (!area) return false;
  if (reason === "pan" || reason === "zoom" || reason === "rotate") return false;
  if (reason === "area") return true;
  const same = watch.areaKey === areaKey(area);
  if (reason === "settled") return same && watch.settling === true;
  if (watch.settling) return false;
  if (reason === "upgrade") return same;
  return watch.awaitingTiles && same;
}

export function watchAfterExtract(area: WorkArea, tilesFinished: boolean): ExtractWatch {
  return { areaKey: areaKey(area), awaitingTiles: !tilesFinished };
}

/** A new or restored rectangle: nothing is read until the camera settles on it. */
export function watchForNewArea(area: WorkArea): ExtractWatch {
  return { areaKey: areaKey(area), awaitingTiles: true, settling: true };
}

/** Zoom of the road tiles the view is drawing. They stop at ROAD_EXTRACT_ZOOM. */
export function roadTileZoom(mapZoom: number): number {
  if (!Number.isFinite(mapZoom)) return 0;
  return Math.max(0, Math.min(ROAD_EXTRACT_ZOOM, Math.floor(mapZoom)));
}

/**
 * After a read from tiles below ROAD_EXTRACT_ZOOM, read again once the view
 * shows the canvas with closer tiles. A read at full zoom is final.
 */
export function shouldUpgradeRoads(input: {
  roadCount: number;
  readTileZoom: number;
  viewTileZoom: number;
  viewShowsCanvas: boolean;
}): boolean {
  if (!input.viewShowsCanvas) return false;
  if (input.readTileZoom >= ROAD_EXTRACT_ZOOM) return false;
  if (input.viewTileZoom <= input.readTileZoom) return false;
  return input.roadCount < FEW_ROADS || input.viewTileZoom >= ROAD_EXTRACT_ZOOM;
}

/** Union of two road sets. Repeated segments (either direction) are kept once. */
export function unionRoads(
  a: ReadonlyArray<readonly [LngLat, LngLat]>,
  b: ReadonlyArray<readonly [LngLat, LngLat]>,
): Array<[LngLat, LngLat]> {
  const seen = new Set<string>();
  const out: Array<[LngLat, LngLat]> = [];
  const key = (p: LngLat) => `${p[0].toFixed(7)},${p[1].toFixed(7)}`;
  for (const list of [a, b]) {
    for (const [p, q] of list) {
      const kp = key(p);
      const kq = key(q);
      const k = kp < kq ? `${kp}|${kq}` : `${kq}|${kp}`;
      if (seen.has(k)) continue;
      seen.add(k);
      out.push([[p[0], p[1]], [q[0], q[1]]]);
    }
  }
  return out;
}

export function boundsOverlap(a: WorkArea, b: WorkArea): boolean {
  return a.west <= b.east && b.west <= a.east && a.south <= b.north && b.south <= a.north;
}

/** Keep the part of each line that sits inside the (possibly rotated) rectangle. */
export function clipLinesToWorkArea(lines: readonly LngLat[][], area: WorkArea): LngLat[][] {
  const out: LngLat[][] = [];
  for (const line of lines) {
    let current: LngLat[] | null = null;
    const flush = () => {
      if (current && current.length >= 2) out.push(current);
      current = null;
    };
    for (let i = 1; i < line.length; i++) {
      const a = line[i - 1];
      const b = line[i];
      if (!a || !b) continue;
      const clipped = clipSegment(a, b, area);
      if (!clipped) {
        flush();
        continue;
      }
      if (!current) {
        current = [clipped[0], clipped[1]];
        continue;
      }
      const last = current[current.length - 1];
      if (last && near(last, clipped[0])) current.push(clipped[1]);
      else {
        flush();
        current = [clipped[0], clipped[1]];
      }
    }
    flush();
  }
  return out;
}

export function translateCanvas(area: WorkArea, dLng: number, dLat: number): WorkArea {
  const next: WorkArea = {
    west: area.west + dLng,
    south: area.south + dLat,
    east: area.east + dLng,
    north: area.north + dLat,
  };
  const bearing = canvasBearing(area);
  if (bearing !== 0) next.bearing = bearing;
  return next;
}

/** Set absolute bearing; 0 drops the field. */
export function rotateCanvas(area: WorkArea, bearing: number): WorkArea {
  const br = normalizeBearing(bearing);
  if (br === 0) {
    return { west: area.west, south: area.south, east: area.east, north: area.north };
  }
  return { west: area.west, south: area.south, east: area.east, north: area.north, bearing: br };
}

/**
 * Resize from a side or corner handle. `local` is the pointer in canvas-local
 * meters (same frame as {@link toLocalM}). Body and rotate are ignored.
 */
export function resizeCanvas(area: WorkArea, handle: CanvasHandle, local: readonly [number, number]): WorkArea | null {
  if (handle === "body" || handle === "rotate") return area;
  const { halfW, halfH } = canvasHalfSizeM(area);
  let minX = -halfW;
  let maxX = halfW;
  let minY = -halfH;
  let maxY = halfH;
  const [x, y] = local;
  if (handle.includes("w") || handle === "w") minX = Math.min(x, maxX - MIN_HALF_M * 2);
  if (handle.includes("e") || handle === "e") maxX = Math.max(x, minX + MIN_HALF_M * 2);
  if (handle.includes("s") || handle === "s") minY = Math.min(y, maxY - MIN_HALF_M * 2);
  if (handle.includes("n") || handle === "n") maxY = Math.max(y, minY + MIN_HALF_M * 2);

  const newHalfW = (maxX - minX) / 2;
  const newHalfH = (maxY - minY) / 2;
  if (newHalfW < MIN_HALF_M || newHalfH < MIN_HALF_M) return null;

  // Keep the untouched edges fixed: shift center by the midpoint of the new local box.
  const midX = (minX + maxX) / 2;
  const midY = (minY + maxY) / 2;
  const newCenter = fromLocalM([midX, midY], area);
  return canvasFromCenterSize(newCenter, newHalfW * 2, newHalfH * 2, canvasBearing(area));
}

/** Local meter offset of a lng/lat inside the canvas frame. */
export function toLocalM(point: LngLat, area: WorkArea): [number, number] {
  const c = canvasCenter(area);
  const cos = Math.cos((c[1] * Math.PI) / 180);
  const east = (point[0] - c[0]) * METERS_PER_DEG_LAT * Math.max(0.01, cos);
  const north = (point[1] - c[1]) * METERS_PER_DEG_LAT;
  const θ = (canvasBearing(area) * Math.PI) / 180;
  const cosB = Math.cos(θ);
  const sinB = Math.sin(θ);
  return [east * cosB - north * sinB, east * sinB + north * cosB];
}

/** Inverse of {@link toLocalM}. */
export function fromLocalM(local: readonly [number, number], area: WorkArea): LngLat {
  const c = canvasCenter(area);
  const cos = Math.cos((c[1] * Math.PI) / 180);
  const θ = (canvasBearing(area) * Math.PI) / 180;
  const cosB = Math.cos(θ);
  const sinB = Math.sin(θ);
  const [lx, ly] = local;
  const east = lx * cosB + ly * sinB;
  const north = -lx * sinB + ly * cosB;
  const lngScale = METERS_PER_DEG_LAT * Math.max(0.01, cos);
  return [c[0] + east / lngScale, c[1] + north / METERS_PER_DEG_LAT];
}

/** Screen-space handle anchors: four corners, four edge midpoints, rotate knob above top. */
export function canvasHandleLocals(area: WorkArea): Array<{ handle: CanvasHandle; local: [number, number] }> {
  const { halfW, halfH } = canvasHalfSizeM(area);
  const knob = halfH + Math.max(24, halfH * 0.12);
  return [
    { handle: "sw", local: [-halfW, -halfH] },
    { handle: "se", local: [halfW, -halfH] },
    { handle: "ne", local: [halfW, halfH] },
    { handle: "nw", local: [-halfW, halfH] },
    { handle: "s", local: [0, -halfH] },
    { handle: "e", local: [halfW, 0] },
    { handle: "n", local: [0, halfH] },
    { handle: "w", local: [-halfW, 0] },
    { handle: "rotate", local: [0, knob] },
  ];
}

function clipSegment(a: LngLat, b: LngLat, area: WorkArea): [LngLat, LngLat] | null {
  if (canvasBearing(area) === 0) return clipSegmentAxis(a, b, area);
  const { halfW, halfH } = canvasHalfSizeM(area);
  const la = toLocalM(a, area);
  const lb = toLocalM(b, area);
  const clipped = clipSegmentLocal(la, lb, halfW, halfH);
  if (!clipped) return null;
  return [fromLocalM(clipped[0], area), fromLocalM(clipped[1], area)];
}

function clipSegmentAxis(a: LngLat, b: LngLat, area: WorkArea): [LngLat, LngLat] | null {
  let t0 = 0;
  let t1 = 1;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const p = [-dx, dx, -dy, dy];
  const q = [a[0] - area.west, area.east - a[0], a[1] - area.south, area.north - a[1]];
  for (let i = 0; i < 4; i++) {
    const pi = p[i] ?? 0;
    const qi = q[i] ?? 0;
    if (pi === 0) {
      if (qi < 0) return null;
      continue;
    }
    const t = qi / pi;
    if (pi < 0) {
      if (t > t1) return null;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return null;
      if (t < t1) t1 = t;
    }
  }
  return [
    [a[0] + t0 * dx, a[1] + t0 * dy],
    [a[0] + t1 * dx, a[1] + t1 * dy],
  ];
}

function clipSegmentLocal(
  a: readonly [number, number],
  b: readonly [number, number],
  halfW: number,
  halfH: number,
): [[number, number], [number, number]] | null {
  let t0 = 0;
  let t1 = 1;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const p = [-dx, dx, -dy, dy];
  const q = [a[0] + halfW, halfW - a[0], a[1] + halfH, halfH - a[1]];
  for (let i = 0; i < 4; i++) {
    const pi = p[i] ?? 0;
    const qi = q[i] ?? 0;
    if (pi === 0) {
      if (qi < 0) return null;
      continue;
    }
    const t = qi / pi;
    if (pi < 0) {
      if (t > t1) return null;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return null;
      if (t < t1) t1 = t;
    }
  }
  return [
    [a[0] + t0 * dx, a[1] + t0 * dy],
    [a[0] + t1 * dx, a[1] + t1 * dy],
  ];
}

function normalizeBearing(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  let b = deg % 360;
  if (b > 180) b -= 360;
  if (b <= -180) b += 360;
  return Math.abs(b) < 1e-9 ? 0 : b;
}

function finite(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function near(a: LngLat, b: LngLat): boolean {
  return Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9;
}
